import React, { useEffect, useState } from 'react';
import { Save, Phone, Mail, Store } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    store_name: '',
    contact_number: '',
    whatsapp_number: '',
    email: '',
    whatsapp_chat_enabled: true,
    whatsapp_chat_message: 'Hi, I need help with ANANT WEB PORTAL',
    whatsapp_support_url: '',
    live_chat_url: '',
    payment_gateways: {
      stripe: true,
      razorpay: false,
      paypal: false,
      manual: true
    },
    manual_payment_qr: '',
    manual_payment_upi: '',
    add_fund_link: '',
    notification_sound_url: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
      setFormData({
        store_name: response.data.store_name,
        contact_number: response.data.contact_number,
        whatsapp_number: response.data.whatsapp_number,
        email: response.data.email,
        whatsapp_chat_enabled: response.data.whatsapp_chat_enabled !== false,
        whatsapp_chat_message: response.data.whatsapp_chat_message || 'Hi, I need help with ANANT WEB PORTAL',
        whatsapp_support_url: response.data.whatsapp_support_url || '',
        live_chat_url: response.data.live_chat_url || '',
        payment_gateways: response.data.payment_gateways,
        manual_payment_qr: response.data.manual_payment_qr || '',
        manual_payment_upi: response.data.manual_payment_upi || '',
        add_fund_link: response.data.add_fund_link || '',
        notification_sound_url: response.data.notification_sound_url || ''
      });
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await api.put('/settings', formData);
      toast.success('Settings updated successfully');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-2xl">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="admin-settings-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-bold mb-8" data-testid="settings-title">Store Settings</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Store Information */}
          <div className="glass-card p-6">
            <h2 className="text-2xl font-semibold mb-6">Store Information</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  <Store className="w-4 h-4 inline mr-2" />
                  Store Name
                </label>
                <input
                  type="text"
                  value={formData.store_name}
                  onChange={(e) => setFormData({ ...formData, store_name: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                  data-testid="store-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  <Phone className="w-4 h-4 inline mr-2" />
                  Contact Number
                </label>
                <input
                  type="tel"
                  value={formData.contact_number}
                  onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                  data-testid="contact-number-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  <Phone className="w-4 h-4 inline mr-2" />
                  WhatsApp Number
                </label>
                <input
                  type="tel"
                  value={formData.whatsapp_number}
                  onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                  data-testid="whatsapp-number-input"
                />
                <p className="text-xs text-muted-foreground mt-1">For floating chat button (e.g., 919579369830)</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                  data-testid="email-input"
                />
              </div>
            </div>
          </div>

          {/* WhatsApp Chat Configuration */}
          <div className="glass-card p-6">
            <h2 className="text-2xl font-semibold mb-6">WhatsApp Chat Widget</h2>
            
            <div className="space-y-4">
              <div>
                <label className="flex items-center gap-3 mb-4">
                  <input
                    type="checkbox"
                    checked={formData.whatsapp_chat_enabled}
                    onChange={(e) => setFormData({ ...formData, whatsapp_chat_enabled: e.target.checked })}
                    className="w-5 h-5"
                    data-testid="whatsapp-chat-enabled"
                  />
                  <span className="font-medium">Enable WhatsApp Chat Button</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Default Chat Message</label>
                <textarea
                  value={formData.whatsapp_chat_message}
                  onChange={(e) => setFormData({ ...formData, whatsapp_chat_message: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                  placeholder="Hi, I need help with..."
                  data-testid="whatsapp-chat-message-input"
                />
                <p className="text-xs text-muted-foreground mt-1">This message will pre-fill when users click the WhatsApp button</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">WhatsApp Support URL</label>
                <input
                  type="url"
                  value={formData.whatsapp_support_url}
                  onChange={(e) => setFormData({ ...formData, whatsapp_support_url: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                  placeholder="https://wa.me/919579369830"
                  data-testid="whatsapp-support-url-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Direct WhatsApp link for support</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Live Chat URL / Embed Code</label>
                <textarea
                  value={formData.live_chat_url}
                  onChange={(e) => setFormData({ ...formData, live_chat_url: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                  placeholder="https://tawk.to/... or <script>...</script>"
                  data-testid="live-chat-url-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Tawk.to, Tidio, or any live chat embed code/URL</p>
              </div>

              <div className="mt-4 p-4 bg-accent/10 rounded-lg">
                <p className="text-sm font-medium mb-2">Preview:</p>
                <div className="flex items-center gap-3 bg-[#25D366] text-white px-4 py-3 rounded-full w-fit">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span className="font-medium">Chat with us</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Gateways */}
          <div className="glass-card p-6">
            <h2 className="text-2xl font-semibold mb-6">Payment Gateways</h2>
            
            <div className="space-y-3">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.payment_gateways.stripe}
                  onChange={(e) => setFormData({
                    ...formData,
                    payment_gateways: { ...formData.payment_gateways, stripe: e.target.checked }
                  })}
                  className="w-5 h-5"
                  data-testid="gateway-stripe"
                />
                <span className="font-medium">Stripe (Credit/Debit Cards)</span>
              </label>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.payment_gateways.razorpay}
                  onChange={(e) => setFormData({
                    ...formData,
                    payment_gateways: { ...formData.payment_gateways, razorpay: e.target.checked }
                  })}
                  className="w-5 h-5"
                  data-testid="gateway-razorpay"
                />
                <span className="font-medium">Razorpay (UPI, Cards, Net Banking)</span>
              </label>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.payment_gateways.paypal}
                  onChange={(e) => setFormData({
                    ...formData,
                    payment_gateways: { ...formData.payment_gateways, paypal: e.target.checked }
                  })}
                  className="w-5 h-5"
                  data-testid="gateway-paypal"
                />
                <span className="font-medium">PayPal</span>
              </label>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.payment_gateways.manual}
                  onChange={(e) => setFormData({
                    ...formData,
                    payment_gateways: { ...formData.payment_gateways, manual: e.target.checked }
                  })}
                  className="w-5 h-5"
                  data-testid="gateway-manual"
                />
                <span className="font-medium">Manual Payment (QR Code / UPI)</span>
              </label>
            </div>
          </div>

          {/* Manual Payment Details */}
          <div className="glass-card p-6">
            <h2 className="text-2xl font-semibold mb-6">Manual Payment Details</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">UPI ID</label>
                <input
                  type="text"
                  value={formData.manual_payment_upi}
                  onChange={(e) => setFormData({ ...formData, manual_payment_upi: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                  placeholder="yourname@upi"
                  data-testid="upi-id-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Enter your UPI ID for direct payments</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">QR Code Image URL</label>
                <input
                  type="url"
                  value={formData.manual_payment_qr}
                  onChange={(e) => setFormData({ ...formData, manual_payment_qr: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                  placeholder="https://example.com/qr-code.png"
                  data-testid="qr-code-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Upload your payment QR code image and paste URL here</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Add Fund External Link</label>
                <input
                  type="url"
                  value={formData.add_fund_link}
                  onChange={(e) => setFormData({ ...formData, add_fund_link: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                  placeholder="https://payment-gateway.com/..."
                  data-testid="add-fund-link-input"
                />
                <p className="text-xs text-muted-foreground mt-1">External payment link for "Add Fund" button (e.g., Razorpay payment page)</p>
              </div>

              {formData.manual_payment_qr && (
                <div className="mt-4">
                  <p className="text-sm font-medium mb-2">QR Code Preview:</p>
                  <img 
                    src={formData.manual_payment_qr} 
                    alt="Payment QR Code" 
                    className="w-48 h-48 object-contain bg-white rounded-lg p-2"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Notification Settings */}
          <div className="glass-card p-6">
            <h2 className="text-2xl font-semibold mb-6">Notification Settings</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Custom Notification Sound URL</label>
                <input
                  type="url"
                  value={formData.notification_sound_url}
                  onChange={(e) => setFormData({ ...formData, notification_sound_url: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                  placeholder="https://example.com/notification.mp3"
                  data-testid="notification-sound-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Leave empty for default sound. MP3 or WAV format recommended.</p>
              </div>

              {formData.notification_sound_url && (
                <div className="mt-4">
                  <p className="text-sm font-medium mb-2">Test Sound:</p>
                  <audio controls className="w-full">
                    <source src={formData.notification_sound_url} />
                  </audio>
                </div>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="save-settings-button"
          >
            <Save className="w-5 h-5" />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminSettings;
